import { XCircle, ShoppingBag } from "lucide-react";
import type { Customer } from "../../../types/store";

interface Props {
  customer: Customer | null;
  onClose: () => void;
  isOpen: boolean;
}

export default function ViewCustomerModal({ customer, onClose, isOpen }: Props) {
  if (!isOpen || !customer) return null;

  const fullName = [customer.firstname, customer.othername, customer.lastname]
    .filter(Boolean)
    .join(" ");

  const sales = customer.Sale ?? [];

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-[999]">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-semibold text-primary">Customer Details</h2>
          <XCircle onClick={onClose} color="red" className="hover:cursor-pointer shrink-0" />
        </div>

        {/* Basic info */}
        <div className="space-y-1 text-sm mb-4">
          <p><span className="font-medium">Name:</span> {fullName}</p>
          <p><span className="font-medium">Email:</span> {customer.email || "-"}</p>
          <p><span className="font-medium">Phone:</span> {customer.phone_number || "-"}</p>
          <p><span className="font-medium">Total Purchases:</span> {sales.length}</p>
        </div>

        {/* Purchase history */}
        <div className="flex-1 overflow-y-auto">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Purchase History</h3>

          {sales.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-gray-400">
              <ShoppingBag size={32} className="text-gray-300" />
              <p className="text-sm">No purchases yet</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {sales.map((sale) => (
                <li key={sale.id} className="border border-gray-200 rounded-lg p-3 text-sm">
                  {/* Sale header */}
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-500 text-xs">
                      {sale.created_at
                        ? new Date(sale.created_at).toLocaleDateString(undefined, {
                            year: "numeric", month: "short", day: "numeric",
                          })
                        : "—"}
                    </span>
                    <span className="font-semibold text-blue-700">
                      GHC {sale.amount != null ? Number(sale.amount).toFixed(2) : "—"}
                    </span>
                  </div>

                  {/* Items */}
                  <ul className="space-y-1">
                    {(sale.ItemPurchase ?? []).map((item) => (
                      <li key={item.id} className="flex justify-between text-gray-700">
                        <span>{item.Product?.name ?? "Unknown product"}</span>
                        <span className="text-gray-500">
                          × {item.quantity}
                          {item.Product && (
                            <> &nbsp;·&nbsp; GHC {(Number(item.Product.selling_price) * item.quantity).toFixed(2)}</>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Payment method */}
                  {sale.payment_method && (
                    <p className="mt-2 text-xs text-gray-400">{sale.payment_method}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button onClick={onClose} className="bg-primary text-white px-4 py-2 rounded-md text-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
